/**
 * Pure aggregation of per-call RPC samples into per-group usage buckets.
 *
 * Kept free of side-effecting imports (no client logger, no env validation) so
 * it can be unit-tested in isolation and reused without pulling in a transport.
 */
import { isInterruptedRpcError, type RpcErrorType } from './error-classifier'

export interface RpcCallSample {
  chainId: number
  method: string
  durationSeconds: number
  success: boolean
  errorType?: RpcErrorType
  /** Log-safe error text from `describeRpcError`, set on failures. */
  errorMessage?: string
  handled?: boolean
}

export interface RpcUsageBucket {
  chain_id: string
  method: string
  status: 'success' | 'error' | 'interrupted' | 'handled'
  error_type?: string
  /** Text of the first failure in the bucket; failures sharing a bucket share a classification. */
  error_message?: string
  count: number
  total_duration_s: number
  max_duration_s: number
}

/** Buckets are keyed by (chain, method, status, error_type). */
export function aggregateRpcSamples(samples: readonly RpcCallSample[]): RpcUsageBucket[] {
  const buckets = new Map<string, RpcUsageBucket>()

  for (const sample of samples) {
    const status: RpcUsageBucket['status'] = sample.success
      ? 'success'
      : sample.handled
        ? 'handled'
        : isInterruptedRpcError(sample.errorType)
          ? 'interrupted'
          : 'error'
    const chain_id = String(sample.chainId ?? 'unknown')
    const method = sample.method ?? 'unknown'
    const error_type = status === 'success' ? undefined : (sample.errorType ?? 'unknown')
    const duration = typeof sample.durationSeconds === 'number' ? sample.durationSeconds : 0
    const key = `${chain_id}|${method}|${status}|${error_type ?? ''}`

    const existing = buckets.get(key)
    if (existing) {
      existing.count += 1
      existing.total_duration_s += duration
      existing.max_duration_s = Math.max(existing.max_duration_s, duration)
      if (error_type && !existing.error_message && sample.errorMessage)
        existing.error_message = sample.errorMessage
    } else {
      buckets.set(key, {
        chain_id,
        method,
        status,
        error_type,
        ...(error_type && sample.errorMessage ? { error_message: sample.errorMessage } : {}),
        count: 1,
        total_duration_s: duration,
        max_duration_s: duration
      })
    }
  }

  return [...buckets.values()]
}
